import React, { useMemo, useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Edit2, Eye, FileText, Image, Settings, Globe } from 'lucide-react';
import { FRONTEND_PAGES, PAGE_SLUG_MAP } from './pageConstants';

function AdminPageSeo() {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Tümü');
  const [editingPage, setEditingPage] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [pagesFromDB, setPagesFromDB] = useState([]);
  const [loadingPages, setLoadingPages] = useState(true);

  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [selectedImageField, setSelectedImageField] = useState(null);

  const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';

  // Dinamik sayfaları çek
  useEffect(() => {
    const fetchDynamicPages = async () => {
      try {
        const { adminApiFetch } = await import('../../../utils/adminApi');
        const res = await adminApiFetch(`${apiBase}/backend/admin_api.php?action=getDynamicPagesForSeo`);
        const data = await res.json();
        
        if (data.status === 'success' && data.data) {
          const dynamicPages = data.data.map(page => ({
            path: page.path,
            name: page.name,
            category: page.category || 'Dinamik',
            component: null,
            isDynamic: true,
            type: page.type,
            id: page.id
          }));
          
          setPagesFromDB(dynamicPages);
        }
      } catch (error) {
        console.error('Dinamik sayfalar yüklenirken hata:', error);
      } finally {
        setLoadingPages(false);
      }
    };
    
    fetchDynamicPages();
  }, [apiBase]);

  const allPages = useMemo(() => {
    const dynamicPagePaths = new Set(pagesFromDB.map(p => p.path));
    const staticPages = FRONTEND_PAGES.filter(fp => !dynamicPagePaths.has(fp.path));
    return [...pagesFromDB, ...staticPages];
  }, [pagesFromDB]);

  const categories = useMemo(() => {
    const cats = ['Tümü', ...new Set(allPages.map((p) => p.category))];
    return cats;
  }, [allPages]);

  const filteredPages = useMemo(() => {
    return allPages.filter((page) => {
      const matchesSearch =
        page.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        page.path.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'Tümü' || page.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, categoryFilter, allPages]);

  const handleEdit = async (page) => {
    console.log('AdminPageSeo handleEdit çağrıldı:', { path: page.path, name: page.name, isDynamic: page.isDynamic });
    
    setEditingPage(page);
    setLoadingContent(true);
    setPageContent(null);

    const slug = PAGE_SLUG_MAP[page.path];
    
    // Dinamik sayfa path'leri listesi
    const dynamicPaths = [
      '/blog', '/egitim', '/sss', '/sikca-sorulan-sorular',
      '/projeler', '/projeler-katalog',
      '/web-sitesi-tasarimlari', '/hazir-web-sitesi-tasarimlari',
      '/hizmetler'
    ];
    
    // Dinamik sayfa ise getPageSeo API'sini kullan
    if (page.isDynamic || dynamicPaths.includes(page.path) || page.type) {
      console.log('Dinamik sayfa algılandı, getPageSeo API kullanılıyor:', page.path);
      try {
        const { adminApiFetch } = await import('../../../utils/adminApi');
        const res = await adminApiFetch(`${apiBase}/backend/admin_api.php?action=getPageSeo&path=${encodeURIComponent(page.path)}`);
        const data = await res.json();
        
        if (data.status === 'success' && data.data) {
          // pages_seo formatını pageContent formatına dönüştür
          setPageContent({
            meta_title: data.data.title || '',
            meta_description: data.data.description || '',
            meta_keywords: data.data.keywords || '',
            og_image: data.data.og_image || '',
            noindex: data.data.noindex || 0
          });
        } else {
          // SEO verisi yoksa boş yapı oluştur
          setPageContent({
            meta_title: '',
            meta_description: '',
            meta_keywords: '',
            og_image: '',
            noindex: 0
          });
        }
      } catch (error) {
        console.error('Dinamik SEO verisi yükleme hatası:', error);
        setPageContent({
          meta_title: '',
          meta_description: '',
          meta_keywords: '',
          og_image: '',
          noindex: 0
        });
      }
      setLoadingContent(false);
      return;
    }

    // Statik sayfa için eski mantık
    if (slug) {
      try {
        const { adminApiFetch } = await import('../../../utils/adminApi');
        const res = await adminApiFetch(`${apiBase}/backend/admin_api.php?action=getPageContent&slug=${slug}`);
        const data = await res.json();

        if (data.status === 'success') {
          setPageContent(data.data);
        } else {
          console.log('SEO verisi bulunamadı:', data.message);
        }
      } catch (error) {
        console.error('SEO verisi yükleme hatası:', error);
      }
    }

    setLoadingContent(false);
  };

  const handlePreview = (page) => {
    window.open(page.path, '_blank');
  };

  const runMigration = async () => {
    setMigrationRunning(true);
    try {
      const res = await fetch(`${apiBase}/backend/admin_api.php?action=runPageContentsMigration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();

      if (data.status === 'success') {
        setSuccessMsg('Migration başarıyla tamamlandı! SEO ayarlarını düzenleyebilirsiniz.');
        setTimeout(() => setSuccessMsg(''), 3000);

        if (editingPage && PAGE_SLUG_MAP[editingPage.path]) {
          handleEdit(editingPage);
        }
      } else {
        alert('Migration hatası: ' + data.message);
      }
    } catch (error) {
      console.error('Migration hatası:', error);
      alert('Migration çalıştırılırken hata oluştu');
    }
    setMigrationRunning(false);
  };

  const handleSavePageSeo = async () => {
    if (!pageContent || !editingPage) return;

    setSaving(true);
    try {
      // Dinamik sayfa path'leri listesi
      const dynamicPaths = [
        '/blog', '/egitim', '/sss', '/sikca-sorulan-sorular',
        '/projeler', '/projeler-katalog',
        '/web-sitesi-tasarimlari', '/hazir-web-sitesi-tasarimlari',
        '/hizmetler'
      ];
      
      // Dinamik sayfa ise updatePageSeo API'sini kullan
      if (editingPage.isDynamic || dynamicPaths.includes(editingPage.path) || editingPage.type) {
        console.log('Dinamik sayfa SEO kaydediliyor:', editingPage.path);
        const { adminApiFetch } = await import('../../../utils/adminApi');
        const payload = {
          path: editingPage.path,
          title: pageContent.meta_title || '',
          description: pageContent.meta_description || '',
          keywords: pageContent.meta_keywords || '',
          og_image: pageContent.og_image || '',
          noindex: pageContent.noindex || 0
        };
        
        const res = await adminApiFetch(`${apiBase}/backend/admin_api.php?action=updatePageSeo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();

        if (data.status === 'success') {
          setSuccessMsg('SEO ayarları başarıyla güncellendi!');
          setTimeout(() => setSuccessMsg(''), 3000);
          setEditingPage(null);
          setPageContent(null);
        } else {
          alert('Kayıt hatası: ' + data.message);
        }
      } else {
        // Statik sayfa için eski mantık
        const payload = {
          page_slug: pageContent.page_slug,
          meta_title: pageContent.meta_title || '',
          meta_description: pageContent.meta_description || '',
          meta_keywords: pageContent.meta_keywords || '',
          og_image: pageContent.og_image || ''
        };
        const res = await fetch(`${apiBase}/backend/admin_api.php?action=updatePageContent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();

        if (data.status === 'success') {
          setSuccessMsg('SEO ayarları başarıyla güncellendi!');
          setTimeout(() => setSuccessMsg(''), 3000);
          setEditingPage(null);
          setPageContent(null);
        } else {
          alert('Kayıt hatası: ' + data.message);
        }
      }
    } catch (error) {
      console.error('Kayıt hatası:', error);
      alert('SEO ayarları kaydedilirken hata oluştu');
    }
    setSaving(false);
  };

  const openMediaLibrary = async (fieldName) => {
    setSelectedImageField(fieldName);
    setShowMediaLibrary(true);
    setLoadingMedia(true);

    try {
      const res = await fetch(`${apiBase}/backend/admin_api.php?action=getMedia`);
      const data = await res.json();

      if (data.data) {
        setMediaFiles(data.data);
      }
    } catch (error) {
      console.error('Medya yükleme hatası:', error);
    }

    setLoadingMedia(false);
  };

  const selectMedia = (mediaUrl) => {
    if (selectedImageField && pageContent) {
      setPageContent({
        ...pageContent,
        [selectedImageField]: mediaUrl
      });
    }
    setShowMediaLibrary(false);
    setSelectedImageField(null);
  };

  const renderSeoSection = () => {
    if (!pageContent) return null;

    return (
      <div className="bg-gradient-to-br from-purple-50 via-white to-rose-50 border border-purple-200 rounded-lg p-6 shadow-sm w-full overflow-x-hidden box-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Globe className="w-5 h-5 text-purple-600" />
              SEO Ayarları
            </h3>
            <p className="text-sm text-gray-500">
              Sayfanın arama motoru sonuçlarındaki görünürlüğünü buradan yönetin.
            </p>
          </div>
          <span className="text-xs uppercase tracking-wider bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
            Meta • Open Graph
          </span>
        </div>

        <div className="grid gap-5 grid-cols-1 lg:grid-cols-2 w-full overflow-x-hidden box-border">
          <div className="space-y-3 min-w-0 max-w-full">
            <label className="block text-sm font-semibold text-gray-700">Meta Başlık</label>
            <Input
              value={pageContent.meta_title || ''}
              onChange={(e) => setPageContent({ ...pageContent, meta_title: e.target.value })}
              placeholder="Sayfa başlığı (60 karakter önerilir)"
              maxLength={160}
              className="w-full min-w-0"
            />
            <p className="text-xs text-gray-400">
              Google sonuçlarında başlık olarak görüntülenir. Şu an {pageContent.meta_title?.length || 0} karakter.
            </p>
          </div>

          <div className="space-y-3 min-w-0">
            <label className="block text-sm font-semibold text-gray-700">Meta Açıklama</label>
            <Textarea
              value={pageContent.meta_description || ''}
              onChange={(e) => setPageContent({ ...pageContent, meta_description: e.target.value })}
              placeholder="Arama sonuçlarında görünen özet metin (150-160 karakter ideal)"
              rows={4}
              maxLength={300}
              className="w-full min-w-0"
            />
            <p className="text-xs text-gray-400">
              Önerilen uzunluk 150-160 karakter. Şu an {pageContent.meta_description?.length || 0} karakter.
            </p>
          </div>
        </div>

        <div className="grid gap-5 grid-cols-1 lg:grid-cols-2 mt-6 w-full overflow-x-hidden">
          <div className="space-y-3 min-w-0">
            <label className="block text-sm font-semibold text-gray-700">Anahtar Kelimeler</label>
            <Input
              value={pageContent.meta_keywords || ''}
              onChange={(e) => setPageContent({ ...pageContent, meta_keywords: e.target.value })}
              placeholder="Her bir anahtar kelimeyi virgül ile ayırın"
              className="w-full min-w-0"
            />
            <p className="text-xs text-gray-400">Örn: dijital ajans, seo hizmeti, web tasarım</p>
          </div>

          <div className="space-y-3 min-w-0">
            <label className="block text-sm font-semibold text-gray-700">Open Graph Görseli</label>
            <div className="flex gap-2 min-w-0">
              <Input
                value={pageContent.og_image || ''}
                onChange={(e) => setPageContent({ ...pageContent, og_image: e.target.value })}
                placeholder="1200x630 px önerilir"
                className="flex-1 min-w-0"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => openMediaLibrary('og_image')}
                className="flex items-center gap-2"
              >
                <Image className="w-4 h-4" />
                Medya Seç
              </Button>
            </div>
            {pageContent.og_image && (
              <div className="mt-2">
                <img
                  src={pageContent.og_image}
                  alt="OG preview"
                  className="w-64 h-36 object-cover rounded border border-gray-200"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full px-4 sm:px-6 py-10 space-y-10 overflow-x-hidden max-w-full">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Sayfa SEO Ayarları</h1>
          <p className="text-sm text-gray-500">
            Meta başlık, açıklama, anahtar kelime ve Open Graph görsellerini merkezi olarak yönetin.
          </p>
        </div>
      </div>

      {successMsg && (
        <div className="p-4 rounded-lg bg-green-50 text-green-700 border border-green-200 flex items-center gap-2">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          {successMsg}
        </div>
      )}

      <section className="rounded-3xl border border-gray-200 bg-white shadow-xl overflow-hidden">
        <div className="border-b border-gray-200 px-8 py-6">
          <h2 className="text-lg font-semibold text-gray-900">SEO Yönetimi</h2>
        </div>

        <div className="px-2 pb-6 pt-4 md:px-6 space-y-6 overflow-x-hidden max-w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 w-full overflow-x-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 w-full overflow-x-hidden">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sayfa Ara</label>
                <Input
                  placeholder="Sayfa adı veya URL..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kategori</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span>
                  Toplam: <strong>{FRONTEND_PAGES.length}</strong> sayfa
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                <span>
                  Gösterilen: <strong>{filteredPages.length}</strong> sayfa
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-full">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sayfa Adı
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kategori
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                  Component
                </th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredPages.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="w-12 h-12 text-gray-300" />
                      <p>Sayfa bulunamadı</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPages.map((page) => (
                  <tr key={page.path} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-gray-900 truncate">{page.name}</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <code className="text-xs sm:text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded break-all">{page.path}</code>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {page.category}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                      <span className="text-sm text-gray-600">{page.component}</span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1 sm:gap-2">
                        <Button variant="outline" size="sm" onClick={() => handlePreview(page)} className="flex items-center gap-1 text-xs sm:text-sm">
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="hidden sm:inline">Önizle</span>
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleEdit(page)}
                          className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm"
                        >
                          <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="hidden sm:inline">Düzenle</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
          </div>

          <div className="border rounded-lg p-4 bg-gradient-to-br from-purple-50 to-blue-50">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              SEO Migration
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Sayfa içerikleri veritabanına aktarılmadıysa migration çalıştırarak SEO alanlarını oluşturun.
            </p>
          </div>
            <Button onClick={runMigration} disabled={migrationRunning} variant="secondary">
              {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
            </Button>
          </div>
          </div>
        </div>
      </section>

      {editingPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10 rounded-t-lg">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">{editingPage.name} - SEO Ayarları</h2>
                <p className="text-sm text-gray-600 mt-1">
                  <code className="bg-gray-100 px-2 py-1 rounded text-purple-600">{editingPage.path}</code>
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setEditingPage(null);
                  setPageContent(null);
                }}
              >
                Kapat
              </Button>
            </div>
            <div className="p-4 sm:p-6 space-y-6 w-full overflow-x-hidden">
              {loadingContent ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800 text-sm">
                  <p className="font-medium mb-2">⚠️ Veritabanında SEO verisi bulunamadı</p>
                  <p className="mb-4">
                    Bu sayfanın SEO ayarlarını düzenleyebilmek için migration çalıştırmanız gerekiyor. İşlem tamamlandıktan
                    sonra sayfayı yeniden açın.
                  </p>
                  <Button onClick={runMigration} disabled={migrationRunning} className="bg-yellow-600 hover:bg-yellow-700 text-white">
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : (
                <>
                  {renderSeoSection()}
                  <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingPage(null);
                        setPageContent(null);
                      }}
                    >
                      İptal
                    </Button>
                    <Button
                      onClick={handleSavePageSeo}
                      disabled={saving}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {saving ? 'Kaydediliyor...' : 'SEO Ayarlarını Kaydet'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showMediaLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10 rounded-t-lg">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Medya Kütüphanesi</h2>
                <p className="text-sm text-gray-600 mt-1">Bir resim seçin</p>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setShowMediaLibrary(false);
                  setSelectedImageField(null);
                }}
              >
                Kapat
              </Button>
            </div>
            <div className="p-6">
              {loadingMedia ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : mediaFiles.length === 0 ? (
                <div className="text-center py-12">
                  <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Henüz medya dosyası yok</p>
                  <p className="text-sm text-gray-400 mt-2">Medya sayfasından dosya yükleyebilirsiniz</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {mediaFiles.map((media, index) => {
                    const fileName = media.name || media.original_name || 'Unknown';
                    const fileType = media.type || media.file_type || '';
                    const fileUrl = media.url || media.file_path || '';

                    return (
                      <div
                        key={media.id || fileUrl || index}
                        onClick={() => selectMedia(fileUrl)}
                        className="relative group cursor-pointer border-2 border-gray-200 rounded-lg overflow-hidden hover:border-purple-500 transition-all"
                      >
                        <div className="aspect-square bg-gray-100 flex items-center justify-center">
                          {fileType.startsWith('image/') ? (
                            <img src={fileUrl} alt={fileName} className="w-full h-full object-cover" />
                          ) : fileType.startsWith('video/') ? (
                            <div className="flex flex-col items-center justify-center text-gray-500">
                              <Eye className="w-12 h-12 mb-2" />
                              <p className="text-xs">Video</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center text-gray-500">
                              <FileText className="w-12 h-12 mb-2" />
                              <p className="text-xs">File</p>
                            </div>
                          )}
                        </div>
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center">
                          <Button
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 transition-opacity bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            Seç
                          </Button>
                        </div>
                        <div className="p-2 bg-white">
                          <p className="text-xs text-gray-600 truncate" title={fileName}>
                            {fileName}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPageSeo;

